import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import { apiAuth } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function GlobalSetting() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const settingData = async () => {
    try {
      setLoading(true);
      const response = await apiAuth.getSetting();
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    settingData();
  }, []);

  const initialValues = {
    id: data?._id || "",
    timer: data?.timer || "",
    instaLink: data?.instaLink || "",
    fbLink: data?.fbLink || "",
    twitterLink: data?.twitterLink || "",
    linkedInLink: data?.linkedInLink || "",
    dayType: data?.dayType || "",
    timerCountdown: data?.timerCountdown || "",
    nextBidCountdown: data?.nextBidCountdown || "",
    bids: data?.bids || "",
    bidPrice: data?.bidPrice || "",
    contactUsEmail: data?.contactUsEmail || "",
    description: data?.description || "",
    location: data?.location || "",
    phone: data?.phone || "",
    perBidDeduction: data?.perBidDeduction || "",
    bidsWithoutPackage: data?.bidsWithoutPackage || "",
    bidsPrice: data?.bidsPrice || "",
    bidPackageTitle: data?.bidPackageTitle || "",
  };

  const validationSchema = Yup.object().shape({
    instaLink: Yup.string()
      .url("Invalid URL")
      .required("Instagram link is required"),
    fbLink: Yup.string()
      .url("Invalid URL")
      .required("Facebook link is required"),
    twitterLink: Yup.string()
      .url("Invalid URL")
      .required("Twitter link is required"),
    linkedInLink: Yup.string()
      .url("Invalid URL")
      .required("LinkedIn link is required"),
    contactUsEmail: Yup.string()
      .email("Invalid email format")
      .required("Contact email is required"),
    dayType: Yup.string().required("Day type is required"),
    timerCountdown: Yup.number()
      .required("Timer countdown is required")
      .min(15)
      .max(300),
    nextBidCountdown: Yup.number()
      .required("Next bid countdown is required")
      .max(50),
    bidsWithoutPackage: Yup.number().required("Bids is required").min(1),
    bidsPrice: Yup.number().required("Bid price is required").min(0),
    description: Yup.string().required("Description is required"),
    location: Yup.string().required("Location is required"),
    phone: Yup.string()
  .matches(
    /^(\+?\d{1,3}[- ]?)?\d{10}$/,
    "Phone number is not valid"
  )
  .required("Phone number is required"),
    perBidDeduction: Yup.number()
      .required("Per bid deduction is required")
      .min(0, "Must be at least 0"),
    bidPackageTitle: Yup.string().required("Title is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      console.log(values);
      apiAuth
        .updateSetting(values)
        .then((res) => {
          if (res.status === 200) {
            toast.success(res?.data?.message);
          }
          setSubmitting(false);
          settingData();
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit } = formik;

  return (
    <Card>
      <CardContent>
        <FormikProvider value={formik}>
          <Typography variant="h4" gutterBottom>
            Global Settings
          </Typography>

          {loading ? (
            <CircularProgress />
          ) : (
            <Form onSubmit={handleSubmit}>
              <Tabs
                value={tabIndex}
                onChange={(e, newIndex) => setTabIndex(newIndex)}
                sx={{ mb: 2 }}
              >
                <Tab label="Social Links" />
                <Tab label="Contact Us" />
                <Tab label="Bidding Timer" />
                <Tab label="Bid Purchase" />
                <Tab label="Bid Deduction Controller" />
              </Tabs>

              <TabPanel value={tabIndex} index={0}>
                <Typography variant="h6">Social Links</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  {[
                    ["instaLink", "Instagram Link"],
                    ["fbLink", "Facebook Link"],
                    ["twitterLink", "Twitter Link"],
                    ["linkedInLink", "LinkedIn Link"],
                  ].map(([name, label]) => (
                    <Grid item xs={12} sm={6} key={name}>
                      <TextField
                        name={name}
                        label={label}
                        {...formik.getFieldProps(name)}
                        fullWidth
                        error={Boolean(touched[name] && errors[name])}
                        helperText={touched[name] && errors[name]}
                      />
                    </Grid>
                  ))}
                </Grid>
              </TabPanel>

              <TabPanel value={tabIndex} index={1}>
                <Typography variant="h6">Contact Settings</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="contactUsEmail"
                      label="Contact Email"
                      {...formik.getFieldProps("contactUsEmail")}
                      fullWidth
                      error={Boolean(
                        touched.contactUsEmail && errors.contactUsEmail
                      )}
                      helperText={
                        touched.contactUsEmail && errors.contactUsEmail
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="phone"
                      label="Phone Number"
                      {...formik.getFieldProps("phone")}
                      fullWidth
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="location"
                      label="Location"
                      {...formik.getFieldProps("location")}
                      fullWidth
                      error={Boolean(touched.location && errors.location)}
                      helperText={touched.location && errors.location}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="description"
                      label="Description"
                      {...formik.getFieldProps("description")}
                      fullWidth
                      multiline
                      rows={4}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabIndex} index={2}>
                <Typography variant="h6">Bidding Timer Settings</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="timerCountdown"
                      label="Bid Timeout (sec)"
                      type="number"
                      {...formik.getFieldProps("timerCountdown")}
                      fullWidth
                      error={Boolean(
                        touched.timerCountdown && errors.timerCountdown
                      )}
                      helperText={
                        touched.timerCountdown && errors.timerCountdown
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="nextBidCountdown"
                      label="Next Bid Countdown (sec)"
                      type="number"
                      {...formik.getFieldProps("nextBidCountdown")}
                      fullWidth
                      error={Boolean(
                        touched.nextBidCountdown && errors.nextBidCountdown
                      )}
                      helperText={
                        touched.nextBidCountdown && errors.nextBidCountdown
                      }
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabIndex} index={3}>
                <Typography variant="h6">
                  Bid Purchase Without Package
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                    <TextField
                      name="title"
                      label="Title"
                      {...formik.getFieldProps("bidPackageTitle")}
                      fullWidth
                      error={Boolean(
                        touched.bidPackageTitle && errors.bidPackageTitle
                      )}
                      helperText={
                        touched.bidPackageTitle && errors.bidPackageTitle
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="bids"
                      label="Number of Bids"
                      type="number"
                      {...formik.getFieldProps("bidsWithoutPackage")}
                      fullWidth
                      error={Boolean(
                        touched.bidsWithoutPackage && errors.bidsWithoutPackage
                      )}
                      helperText={
                        touched.bidsWithoutPackage && errors.bidsWithoutPackage
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="bidPrice"
                      label="Bid Price"
                      type="number"
                      {...formik.getFieldProps("bidsPrice")}
                      fullWidth
                      error={Boolean(touched.bidsPrice && errors.bidsPrice)}
                      helperText={touched.bidsPrice && errors.bidsPrice}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value={tabIndex} index={4}>
                <Typography variant="h6">Bid Deduction Controller</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="perBidDeduction"
                      label="Per Bid Deduction ($)"
                      type="number"
                      {...formik.getFieldProps("perBidDeduction")}
                      fullWidth
                      error={Boolean(
                        touched.perBidDeduction && errors.perBidDeduction
                      )}
                      helperText={
                        touched.perBidDeduction && errors.perBidDeduction
                      }
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <Grid container spacing={2} sx={{ mt: 4 }}>
                <Grid item>
                  <Button
                    size="large"
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    Update
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    size="large"
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      if (!isSubmitting) navigate("/dashboard");
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </FormikProvider>
      </CardContent>
    </Card>
  );
}

export default GlobalSetting;
